<template>
    <div class="modal-card card">
        <div class="card-header">
            <!-- Title -->
            <h4 class="card-header-title">Certificate signing request</h4>
            <!-- Close -->
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="card-body">
            <dl class="pairs">
                <div>
                    <dt>Certificate</dt>
                    <dd>{{ modalData.commonName }}</dd>
                </div>
            </dl>

            <p class="step-say">Send this request to your certificate authority. When they return the signed
                certificate, upload it from the certificate page to start deployment.</p>

            <!-- Loading -->
            <div class="csr-block" v-if="loading">
                <div class="csr-head">
                    <span class="sk" style="width:5rem"></span>
                </div>
                <div class="csr-body">
                    <span class="sk d-block" style="width:100%"></span>
                    <span class="sk d-block mt-2" style="width:92%"></span>
                    <span class="sk d-block mt-2" style="width:96%"></span>
                </div>
            </div>

            <!-- One block per signing request -->
            <template v-else-if="csrEntries.length">
                <div class="csr-block" v-for="(entry, index) in csrEntries" :key="index">
                    <div class="csr-head">
                        <span class="badge-soft bs-info" v-if="entry.keyAlgorithm">{{ entry.keyAlgorithm }}</span>
                        <span class="csr-file">{{ fileNameFor(entry) }}</span>
                        <button type="button" class="btn btn-sm btn-light ms-auto" @click="onCopyCSR(entry, index)">
                            <i :class="['fa', copiedIndex === index ? 'fa-check text-success' : 'fa-copy']" />
                        </button>
                    </div>
                    <pre class="csr-body">{{ entry.csr }}</pre>
                </div>
            </template>

            <!-- Nothing waiting to be signed -->
            <div class="csr-empty" v-else>
                <p class="mb-0">No signing request is waiting for this certificate.</p>
            </div>
        </div>

        <div class="card-footer d-flex align-items-center gap-3">
            <span class="text-danger footer-error" v-if="error">{{ error }}</span>
            <div class="ms-auto d-flex gap-2">
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                <button class="btn btn-primary" :disabled="downloading" @click="handleDownload()">
                    <div v-if="downloading" class="spinner-border spinner-border-sm" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    <div v-else>
                        Download{{ csrEntries.length > 1 ? ` ${csrEntries.length} files` : '' }}
                    </div>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from "vuex";
export default {
    name: "GetCertificateCSR",
    data() {
        return {
            error: "",
            csr: null,
            loading: false,
            downloading: false,
            copiedIndex: null,
        };
    },
    props: {
        modalData: {
            type: [Object, Array, String],
        },
    },
    computed: {
        csrEntries() {
            return this.csr?.csrs || [];
        },
    },
    methods: {
        ...mapActions("aut", {
            getCSR: "getCertificateCSR",
        }),
        fileNameFor(entry) {
            return `${this.modalData.commonName}(${entry.keyAlgorithm}).pem`;
        },
        handleDownload() {
            if (!this.csr || !this.csr.csrs.length) {
                this.error = "CSR not found"
                return;
            }
            this.downloading = true;
            this.csr.csrs.forEach(csrEntry => {
                const filename = `${this.modalData.commonName}(${csrEntry.keyAlgorithm}).pem`;
                const blob = new Blob([csrEntry.csr], { type: "application/x-pem-file" });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            });
            this.downloading = false;
        },
        async onCopyCSR(entry, index) {
            if (!entry || !entry.csr) return;

            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(entry.csr);
                } else {
                    // Fallback for older browsers
                    const input = document.createElement('textarea');
                    input.style.position = 'absolute';
                    input.style.left = '-9999px';
                    input.value = entry.csr;
                    document.body.appendChild(input);
                    input.select();
                    document.execCommand('copy');
                    document.body.removeChild(input);
                }

                this.copiedIndex = index;
                setTimeout(() => { this.copiedIndex = null; }, 2000);

                this.$store.dispatch('addToast', {
                    message: "CSR copied to clipboard.",
                    type: "info"
                });
            } catch (err) {
                this.$store.dispatch('addToast', {
                    message: "Failed to copy CSR.",
                    type: "error"
                });
            }
        }

    },
    async beforeMount() {
        this.loading = true;
        await this.getCSR(this.modalData.commonName).then((response) => {
            this.csr = response.data;
        }).catch((error) => {
            let err = null;
            if (error.response.data.error) err = error.response.data.error;
            else err = error.message;
            let toastOptions = {
                message: err || "Something went wrong while fetching CSR. Please contact support.",
                type: "error"
            }
            this.$store.dispatch("addToast", toastOptions);
        })
        this.loading = false;
    }
};
</script>

<style lang="scss" scoped>
@use '../../theme/scss/user-variables' as *;

.pairs {
    margin: 0 0 1rem;
}

.pairs>div {
    display: grid;
    grid-template-columns: 6rem 1fr;
    gap: 1rem;
    align-items: baseline;
}

.pairs dt {
    font-weight: 400;
    color: #6E84A3;
    font-size: .8125rem;
    margin: 0;

    body.dark-mode & {
        color: $dark-text-secondary;
    }
}

.pairs dd {
    margin: 0;
    font-size: .8125rem;
    word-break: break-all;
}

.step-say {
    font-size: .8125rem;
    color: #6E84A3;
    margin: 0 0 1.25rem;

    body.dark-mode & {
        color: $dark-text-secondary;
    }
}

/* One request per block -------------------------------------------------- */
.csr-block {
    border: 1px solid #E3EBF6;
    border-radius: .5rem;
    overflow: hidden;

    &+& {
        margin-top: 1rem;
    }

    body.dark-mode & {
        border-color: $dark-border-color;
    }
}

.csr-head {
    display: flex;
    align-items: center;
    gap: .6rem;
    flex-wrap: wrap;
    padding: .55rem .75rem;
    border-bottom: 1px solid #E3EBF6;
    background: #F9FBFD;

    body.dark-mode & {
        border-bottom-color: $dark-border-color;
        background: $dark-input-bg;
    }
}

.csr-file {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: .75rem;
    color: #6E84A3;
    word-break: break-all;

    body.dark-mode & {
        color: $dark-text-secondary;
    }
}

.csr-body {
    margin: 0;
    padding: .7rem .75rem;
    max-height: 11rem;
    overflow: auto;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: .75rem;
    line-height: 1.5;
    color: #3B506C;
    white-space: pre-wrap;
    word-break: break-all;

    body.dark-mode & {
        color: $dark-text-primary;
    }
}

.csr-empty {
    border: 1px dashed #D2DDEC;
    border-radius: .5rem;
    padding: 1.5rem 1rem;
    text-align: center;
    font-size: .8125rem;
    color: #6E84A3;

    body.dark-mode & {
        border-color: $dark-border-color;
        color: $dark-text-secondary;
    }
}

.footer-error {
    font-size: .8125rem;
}

/* Badges ----------------------------------------------------------------- */
.badge-soft {
    font-size: .6875rem;
    font-weight: 600;
    border-radius: .25rem;
    padding: .2rem .45rem;
    white-space: nowrap;
}

.bs-info {
    background: #EAF3FD;
    color: #1A5FB8;

    body.dark-mode & {
        background: rgba(44, 123, 229, .18);
        color: #7eb5ff;
    }
}

/* Skeletons -------------------------------------------------------------- */
.sk {
    display: inline-block;
    height: .7rem;
    border-radius: .25rem;
    background: linear-gradient(90deg, #EDF2F9 25%, #F9FBFD 37%, #EDF2F9 63%);
    background-size: 400% 100%;
    animation: csr-sk 1.4s ease infinite;

    body.dark-mode & {
        background: linear-gradient(90deg, $dark-input-bg 25%, $dark-elevated-bg 37%, $dark-input-bg 63%);
        background-size: 400% 100%;
    }
}

@keyframes csr-sk {
    0% {
        background-position: 100% 50%;
    }

    100% {
        background-position: 0 50%;
    }
}

@media (prefers-reduced-motion: reduce) {
    .sk {
        animation: none;
    }
}
</style>
